import { useSearchParams, Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader } from '@/components/layout/AppShell'
import { Card } from '@/components/ui/Card'
import { Pagination, PageInfo } from '@/components/ui/Pagination'
import { Badge } from '@/components/ui/Badge'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { useBids } from '@/hooks/useProOps'
import { formatTHB } from '@/lib/utils'
import type { Bid } from '@/lib/pocketbase'

export function BidsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const page = parseInt(searchParams.get('page') || '1')

  const { data, isLoading } = useBids({ page, perPage: 20 })

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', String(newPage))
    setSearchParams(params)
  }

  return (
    <div>
      <PageHeader
        title="ข้อเสนอราคา"
        description="เรียกดูรายการข้อเสนอราคา"
      />

      {data && (
        <div className="mb-4">
          <PageInfo
            currentPage={data.page}
            pageSize={data.perPage}
            totalItems={data.totalItems}
          />
        </div>
      )}

      {isLoading && <LoadingState />}

      {data && data.items.length === 0 && (
        <Card className="p-8 text-center">
          <Icon icon="solar:hand-money-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-900">ไม่พบข้อเสนอราคา</h3>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="flex flex-col gap-3">
            {data.items.map((bid) => (
              <BidCard key={bid.id} bid={bid} />
            ))}
          </div>
          <div className="mt-6">
            <Pagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  )
}

function BidCard({ bid }: { bid: Bid }) {
  const tender = bid.expand?.tender_id as any
  const company = bid.expand?.company_id as any

  const amount =
    (bid as any).bid_price ||
    (bid as any).bid_amount_satang ||
    null

  return (
    <Link to={`/tenders/${tender?.id || bid.tender_id || (bid as any).tender}`}>
      <Card hover>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" size="sm">
                <Icon icon="solar:hand-money-line-duotone" className="w-3 h-3" />
                เสนอราคา
              </Badge>
            </div>
            {tender && (
              <p className="font-display font-medium text-slate-900 mt-2 line-clamp-2">
                {tender.project_name || tender.title_th || 'ไม่ทราบโครงการ'}
              </p>
            )}
            {company && (
              <p className="text-sm text-slate-500 mt-1">
                ผู้เสนอราคา: {company.name || company.name_th || company.name_en || 'ไม่ทราบผู้เสนอราคา'}
              </p>
            )}
          </div>
          <div className="text-right">
            {amount && (
              <p className="font-medium text-slate-900">{formatTHB(amount)}</p>
            )}
            <p className="text-sm text-slate-500 mt-1">
              {bid.submitted_at ? (
                <DateDisplay date={bid.submitted_at} format="relative" />
              ) : (
                '-'
              )}
            </p>
          </div>
        </div>
      </Card>
    </Link>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="animate-pulse">
          <div className="h-4 w-3/4 bg-slate-200 rounded" />
          <div className="h-3 w-1/2 bg-slate-200 rounded mt-2" />
        </Card>
      ))}
    </div>
  )
}
